import { Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { UserHttpService } from '@services/datasources/user-http.service';
import { FeatureService } from '@services/features/feature.service';
import { OrganizationAction } from '@state/organization.state';
import { UserAction, UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { User } from 'app/auth/models';
import { catchError, combineLatest, forkJoin, map, Observable, of, Subject, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { LoaderService } from '@services/loader/loader.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { SolutionAction } from '@state/solution.state';
import { LayoutService } from '../service/app.layout.service';
import countryData from '@assets/data/countries.json';
import { RoleHttpService } from '@services/datasources/role-http.service';
import { SecurityRoleDto } from 'app/@types/dto/security-role-dto.model';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { co } from '@fullcalendar/core/internal-common';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';

type DisplayOrg = OrganizationDto & {
    profile: string;
    isLicenseValid: boolean;
    validUntil: Date;
};
@Component({
    selector: 'app-change-organization-modal',
    templateUrl: './change-organization-modal.component.html',
    styleUrls: ['./change-organization-modal.component.scss'],
})
export class ChangeOrganizationModalComponent implements OnInit {
    @Select(UserState.getCurrentUser) user$: Observable<User>;
    @Select(UserState.getCurrentUserClaims) userClaims$: Observable<UserClaims>;
    allOrganizations: DisplayOrg[] = [];
    organizations: DisplayOrg[] = [];
    fallBackLogo = '';

    user: User | null = null;

    userClaims: UserClaims | null = null;

    constructor(
        private readonly userService: UserHttpService,
        private readonly store: Store,
        private readonly _router: Router,
        private readonly loaderService: LoaderService,
        private readonly featureService: FeatureService,
        private readonly questionnaireService: QuestionnaireHttpService,
        private readonly layoutService: LayoutService,
        private readonly roleService: RoleHttpService,
        private adminHttpService: AdminHTTPService,
    ) {}

    ngOnInit(): void {
        combineLatest([this.userService.getAvailableOrganization(), this.userClaims$, this.user$]).subscribe(
            ([organizations, userClaim, user]) => {
                this.organizations = new Array<DisplayOrg>();
                const organizationObservables = organizations.map((org) =>
                    this.adminHttpService.getOrganizationPricingPlan(org.uid!).pipe(
                        map((pricingPlan) => ({
                            ...org,
                            profile: userClaim.tasks.find(
                                (task) => task.name === 'orguser' && task.organization_id === org.uid,
                            )
                                ? 'Owner'
                                : 'User',
                            isLicenseValid: new Date(pricingPlan?.validUntil).getTime() > new Date().getTime(),
                            validUntil: new Date(pricingPlan?.validUntil),
                        })),
                    ),
                );

                forkJoin(organizationObservables).subscribe((orgs) => {
                    this.organizations = orgs;
                    this.allOrganizations = [...this.organizations];
                });
                this.userClaims = userClaim;
                this.user = user;
            },
        );
        this.fallBackLogo = `assets/images/logo/logo-h.webp`;
    }

    onSearch(searchValue: string) {
        if (searchValue === null || searchValue === '') this.organizations = this.allOrganizations;
        else
            this.organizations = this.allOrganizations.filter((org) =>
                org.name.toUpperCase().includes(searchValue.toUpperCase()),
            );
    }

    onClickOrg(org: DisplayOrg): void {
        if (!org || !org.uid || !this.userClaims || !org.isLicenseValid) {
            return;
        }

        this.loaderService.setLoading(true);

        forkJoin({
            activateOrg: this.activateOrganization(org.uid),
            questionnaires: this.fetchAndActivateQuestionnaire(org.uid),
        }).subscribe({
            next: () => {
                this.store.dispatch(
                    new UserAction.UpdateWithValue({
                        ...this.userClaims,
                        can_sudo: false,
                        is_sudo: false,
                        organization: org,
                        entities_count: this.userClaims?.entities_count!,
                        max_entities: this.userClaims?.max_entities!,
                        user_email: this.userClaims?.user_email!,
                        tasks: this.userClaims?.tasks!,
                    }),
                );
                if (this.userClaims) {
                    this.featureService.handleUserClaims({
                        ...this.userClaims,
                        can_sudo: false,
                        is_sudo: false,
                        organization: org,
                    });
                }

                this.loaderService.setLoading(false);
                this._router.navigate(['/app/measure']);
            },
            error: (err) => {
                this.loaderService.setLoading(false);
            },
        });
    }

    private activateOrganization(orgId: string): Observable<void> {
        this.store.dispatch(new OrganizationAction.Activate(orgId));
        return of(void 0); // Return an observable to integrate with forkJoin
    }

    private fetchAndActivateQuestionnaire(orgId: string): Observable<void> {
        return this.questionnaireService.getAll(orgId).pipe(
            map((instances) =>
                instances
                    .map(({ questionnaire }) => questionnaire)
                    .filter((q) => !q.deleted)
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()),
            ),
            tap((sortedQuestionnaires) => {
                if (sortedQuestionnaires.length > 0) {
                    const latest = sortedQuestionnaires[0];
                    this.store.dispatch(
                        new QuestionnaireAction.Activate(orgId, latest.firstYearMeasured, latest.entityId),
                    );
                    this.store.dispatch(new QuestionnaireConfigAction.Activate(orgId, latest.entityId));
                    if (latest.id) this.store.dispatch(new SolutionAction.Activate(orgId, latest.id));
                }
            }),
            catchError((err) => {
                console.error('Error fetching or processing questionnaires:', err);
                return throwError(err);
            }),
            map(() => void 0), // Return an observable to integrate with forkJoin
        );
    }

    findLocationLabel(code: string) {
        return countryData.find((cntry) => cntry.id === code)?.name;
    }

    getOrgValidity(org: DisplayOrg) {
        const date = new Date(org.validUntil);
        const formatter = new Intl.DateTimeFormat('fr-FR');

        return `${formatter.format(new Date(org.validUntil))}`;
    }
}
